/**
 * Freehand Drawing Tool - Stroke Properties Panel
 * UI controls for stroke styling and appearance
 */

import React, { useState, useEffect } from 'react';
import { 
  Palette, 
  Minus, 
  Square, 
  Circle, 
  Triangle, 
  Diamond,
  ArrowRight,
  MoreHorizontal,
  Settings
} from 'lucide-react';

const StrokePropertiesPanel = ({
  strokeStyle = {
    color: '#000000',
    width: 2,
    lineCap: 'round',
    lineJoin: 'round',
    opacity: 1,
    startArrow: 'none',
    endArrow: 'none'
  },
  onChange = () => {},
  className = ''
}) => {
  const [localStroke, setLocalStroke] = useState(strokeStyle);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    setLocalStroke(strokeStyle);
  }, [strokeStyle]);

  const handleChange = (property, value) => {
    const newStroke = { ...localStroke, [property]: value };
    setLocalStroke(newStroke);
    onChange(newStroke);
  };

  const presetWidths = [1, 2, 3, 4, 5, 8, 12, 16, 24];
  const presetColors = [
    '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
    '#FF0000', '#FF6600', '#FFCC00', '#66FF00', '#00FF66', '#00FFCC',
    '#0066FF', '#6600FF', '#CC00FF', '#FF0066'
  ];

  const dashPatterns = [
    { name: 'Solid', value: undefined },
    { name: 'Dashed', value: [8, 4] },
    { name: 'Dotted', value: [2, 2] },
    { name: 'Dash-Dot', value: [8, 4, 2, 4] },
    { name: 'Long Dash', value: [16, 8] }
  ];

  const arrowTypes = [
    { name: 'None', value: 'none', icon: Minus },
    { name: 'Triangle', value: 'triangle', icon: Triangle },
    { name: 'Arrow', value: 'arrow', icon: ArrowRight },
    { name: 'Diamond', value: 'diamond', icon: Diamond },
    { name: 'Circle', value: 'circle', icon: Circle }
  ];

  return (
    <div className={`stroke-properties-panel bg-white border border-gray-200 rounded-lg shadow-lg p-4 ${className}`}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800 flex items-center">
            <Palette className="w-4 h-4 mr-2" />
            Stroke Properties
          </h3>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="p-1 hover:bg-gray-100 rounded"
            title="Advanced Options"
          >
            <Settings className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Color */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Color</label>
          <div className="flex items-center space-x-2">
            <input
              type="color"
              value={localStroke.color}
              onChange={(e) => handleChange('color', e.target.value)}
              className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
            />
            <input
              type="text"
              value={localStroke.color}
              onChange={(e) => handleChange('color', e.target.value)}
              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="#000000"
            />
          </div>
          
          {/* Color Presets */}
          <div className="grid grid-cols-8 gap-1 mt-2">
            {presetColors.map((color) => (
              <button
                key={color}
                onClick={() => handleChange('color', color)}
                className={`w-6 h-6 rounded border-2 ${
                  localStroke.color === color ? 'border-blue-500' : 'border-gray-300'
                }`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>

        {/* Width */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Width: {localStroke.width}px
          </label>
          <input
            type="range"
            min="0.5"
            max="50"
            step="0.5"
            value={localStroke.width}
            onChange={(e) => handleChange('width', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          
          {/* Width Presets */}
          <div className="flex flex-wrap gap-1 mt-2">
            {presetWidths.map((width) => (
              <button
                key={width}
                onClick={() => handleChange('width', width)}
                className={`px-2 py-1 text-xs border rounded ${
                  localStroke.width === width
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {width}px
              </button>
            ))}
          </div>
        </div>

        {/* Opacity */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Opacity: {Math.round((localStroke.opacity || 1) * 100)}%
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={localStroke.opacity || 1}
            onChange={(e) => handleChange('opacity', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        {/* Line Style */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Line Style</label>
          <div className="grid grid-cols-2 gap-2">
            {dashPatterns.map((pattern) => (
              <button
                key={pattern.name}
                onClick={() => handleChange('dash', pattern.value)}
                className={`p-2 border rounded text-xs ${
                  JSON.stringify(localStroke.dash) === JSON.stringify(pattern.value)
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center mb-1">
                  <svg width="30" height="4" className="overflow-visible">
                    <line
                      x1="0"
                      y1="2"
                      x2="30"
                      y2="2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeDasharray={pattern.value?.join(',') || 'none'}
                    />
                  </svg>
                </div>
                {pattern.name}
              </button>
            ))}
          </div>
        </div>

        {/* Arrows */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Arrows</label>
          <div className="space-y-2">
            {/* Start Arrow */}
            <div>
              <div className="text-xs text-gray-600 mb-1">Start</div>
              <div className="flex space-x-1">
                {arrowTypes.map((arrow) => {
                  const IconComponent = arrow.icon;
                  return (
                    <button
                      key={`start-${arrow.value}`}
                      onClick={() => handleChange('startArrow', arrow.value)}
                      className={`p-2 border rounded ${
                        localStroke.startArrow === arrow.value
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                      title={arrow.name}
                    >
                      <IconComponent className="w-3 h-3" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* End Arrow */}
            <div>
              <div className="text-xs text-gray-600 mb-1">End</div>
              <div className="flex space-x-1">
                {arrowTypes.map((arrow) => {
                  const IconComponent = arrow.icon;
                  return (
                    <button
                      key={`end-${arrow.value}`}
                      onClick={() => handleChange('endArrow', arrow.value)}
                      className={`p-2 border rounded ${
                        localStroke.endArrow === arrow.value
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                      title={arrow.name}
                    >
                      <IconComponent className="w-3 h-3" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Options */}
        {showAdvanced && (
          <div className="space-y-4 pt-4 border-t border-gray-200">
            {/* Line Cap */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Line Cap</label>
              <div className="flex space-x-2">
                {['butt', 'round', 'square'].map((cap) => (
                  <button
                    key={cap}
                    onClick={() => handleChange('lineCap', cap)}
                    className={`px-3 py-1 text-xs border rounded capitalize ${
                      localStroke.lineCap === cap
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {cap}
                  </button>
                ))}
              </div>
            </div>

            {/* Line Join */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Line Join</label>
              <div className="flex space-x-2">
                {['miter', 'round', 'bevel'].map((join) => (
                  <button
                    key={join}
                    onClick={() => handleChange('lineJoin', join)}
                    className={`px-3 py-1 text-xs border rounded capitalize ${
                      localStroke.lineJoin === join
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {join}
                  </button>
                ))}
              </div>
            </div>

            {/* Miter Limit */}
            {localStroke.lineJoin === 'miter' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Miter Limit: {localStroke.miterLimit || 4}
                </label>
                <input
                  type="range"
                  min="1"
                  max="20"
                  step="0.5"
                  value={localStroke.miterLimit || 4}
                  onChange={(e) => handleChange('miterLimit', parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
              </div>
            )}

            {/* Non-scaling Stroke */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="non-scaling"
                checked={localStroke.nonScaling || false}
                onChange={(e) => handleChange('nonScaling', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="non-scaling" className="text-xs text-gray-700">
                Non-scaling stroke
              </label>
            </div>
          </div>
        )}

        {/* Preview */}
        <div className="pt-4 border-t border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-2">Preview</label>
          <div className="bg-gray-50 p-4 rounded border">
            <svg width="100%" height="40" className="overflow-visible">
              <defs>
                {localStroke.startArrow && localStroke.startArrow !== 'none' && (
                  <marker
                    id="preview-start"
                    markerWidth="10"
                    markerHeight="10"
                    refX="10"
                    refY="5"
                    orient="auto"
                  >
                    <path d="M 0 0 L 10 5 L 0 10 Z" fill={localStroke.color} />
                  </marker>
                )}
                {localStroke.endArrow && localStroke.endArrow !== 'none' && (
                  <marker
                    id="preview-end"
                    markerWidth="10"
                    markerHeight="10"
                    refX="10"
                    refY="5"
                    orient="auto"
                  >
                    <path d="M 0 0 L 10 5 L 0 10 Z" fill={localStroke.color} />
                  </marker>
                )}
              </defs>
              <line
                x1="10"
                y1="20"
                x2="90%"
                y2="20"
                stroke={localStroke.color}
                strokeWidth={localStroke.width}
                strokeOpacity={localStroke.opacity || 1}
                strokeLinecap={localStroke.lineCap}
                strokeLinejoin={localStroke.lineJoin}
                strokeDasharray={localStroke.dash?.join(',') || 'none'}
                markerStart={localStroke.startArrow !== 'none' ? 'url(#preview-start)' : undefined}
                markerEnd={localStroke.endArrow !== 'none' ? 'url(#preview-end)' : undefined}
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrokePropertiesPanel;
